/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *
 */

import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounce } from '@utils/use-debounce';
import Spinner from '@components/spinner';
import I18N from '@utils/i18n.base';
import type { SearchHit, SearchScope } from './types';

interface GlobalSearchProps {
    scopes: SearchScope[];
    defaultScopeKey?: string;
    /** Applied to the root wrapper so the header controls width/visibility. */
    className?: string;
}

const LISTBOX_ID = 'global-search-listbox';

/**
 * GitHub-style immersive search: a compact trigger that opens a live results
 * dropdown with keyboard navigation. Entirely scope-driven (see {@link SearchScope}).
 */
export default function GlobalSearch({ scopes, defaultScopeKey, className = '' }: GlobalSearchProps) {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [scopeKey, setScopeKey] = useState(defaultScopeKey ?? scopes[0]?.key);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [hits, setHits] = useState<SearchHit[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const scope = useMemo(() => scopes.find((s) => s.key === scopeKey) ?? scopes[0], [scopes, scopeKey]);
    const debounced = useDebounce(query, 250);
    const trimmed = query.trim();

    const liveHits = scope?.live ? hits : [];
    const hasViewAll = trimmed !== '';
    const viewAllIndex = liveHits.length;
    const entriesCount = liveHits.length + (hasViewAll ? 1 : 0);
    const showDropdown = open && hasViewAll;

    // Live fetch for the active scope.
    useEffect(() => {
        const q = debounced.trim();
        if (!open || !scope?.live || !scope.search || !q) {
            setHits([]);
            setLoading(false);
            return;
        }
        let canceled = false;
        setLoading(true);
        setActiveIndex(-1);
        (async () => {
            try {
                const found = await scope.search!(q);
                if (!canceled) setHits(found);
            } catch {
                if (!canceled) setHits([]);
            } finally {
                if (!canceled) setLoading(false);
            }
        })();
        return () => {
            canceled = true;
        };
    }, [debounced, scope, open]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open]);

    // Global Cmd/Ctrl+/ shortcut to focus the search input.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === '/') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const go = useCallback(
        (to: string) => {
            setOpen(false);
            setActiveIndex(-1);
            if (to && to !== '#') void navigate(to);
        },
        [navigate]
    );

    const openResults = useCallback(() => {
        if (!scope || !trimmed) return;
        go(scope.resultsPath(trimmed));
    }, [scope, trimmed, go]);

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) setOpen(true);
            setActiveIndex((i) => Math.min(i + 1, entriesCount - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (!trimmed) return;
            if (activeIndex >= 0 && activeIndex < liveHits.length) go(liveHits[activeIndex].to);
            else openResults();
        } else if (e.key === 'Escape') {
            if (open) {
                e.preventDefault();
                setOpen(false);
                inputRef.current?.blur();
            }
        }
    };

    if (!scope) return null;

    const viewAllActive = activeIndex === viewAllIndex;

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {/* Trigger */}
            <div className="group border-theme-border bg-theme-content-bg-light focus-within:border-theme-active focus-within:ring-theme-focus/15 flex h-9 items-center gap-1 rounded-xl border pr-1 pl-3 transition focus-within:ring-4">
                <span className="material-symbols-rounded text-theme-text-light text-base">search</span>
                <input
                    ref={inputRef}
                    type="search"
                    name="global-search-query"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-1p-ignore="true"
                    data-lpignore="true"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={scope.placeholder}
                    aria-label={scope.placeholder}
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls={LISTBOX_ID}
                    aria-activedescendant={activeIndex >= 0 ? `gs-opt-${activeIndex}` : undefined}
                    className="text-theme-text placeholder:text-theme-text-muted min-w-0 flex-1 appearance-none bg-transparent text-sm outline-none!"
                />
                {!query && (
                    <kbd className="bg-theme-content-bg-dark text-theme-text-light/70 hidden rounded-md px-1.5 py-0.5 text-xs font-medium md:block">
                        ⌘/
                    </kbd>
                )}
                <span className="bg-theme-input-border mx-1 h-5 w-px" />
                <select
                    value={scopeKey}
                    onChange={(e) => {
                        setScopeKey(e.target.value);
                        setActiveIndex(-1);
                        inputRef.current?.focus();
                    }}
                    aria-label={I18N.app.search}
                    className="text-theme-text-light max-w-22 cursor-pointer truncate bg-transparent pr-1 text-xs outline-none"
                >
                    {scopes.map((s) => (
                        <option key={s.key} value={s.key}>
                            {s.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Dropdown */}
            {showDropdown && (
                <div className="border-theme-border bg-theme-popup-bg absolute top-[calc(100%+0.5rem)] right-0 left-0 z-50 overflow-hidden rounded-2xl border shadow-lg backdrop-blur-xl">
                    <ul id={LISTBOX_ID} role="listbox" className="max-h-[60vh] overflow-y-auto p-1.5">
                        {scope.live && (
                            <li
                                role="presentation"
                                className="text-theme-text-muted px-2.5 pt-1.5 pb-1 text-xs font-semibold tracking-wide uppercase"
                            >
                                {scope.label}
                            </li>
                        )}

                        {scope.live && loading && (
                            <li role="presentation" className="flex justify-center py-4">
                                <Spinner size="sm" />
                            </li>
                        )}

                        {scope.live &&
                            !loading &&
                            liveHits.map((hit, index) => {
                                const active = index === activeIndex;
                                return (
                                    <li key={hit.id} role="option" id={`gs-opt-${index}`} aria-selected={active}>
                                        <button
                                            type="button"
                                            onClick={() => go(hit.to)}
                                            onMouseEnter={() => setActiveIndex(index)}
                                            className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                                                active ? 'bg-theme-hover' : ''
                                            }`}
                                        >
                                            <span className="material-symbols-rounded text-theme-icon text-lg">
                                                {hit.icon}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="text-theme-text block truncate text-sm">
                                                    {hit.title}
                                                </span>
                                                {hit.subtitle && (
                                                    <span className="text-theme-text-muted block truncate text-xs">
                                                        {hit.subtitle}
                                                    </span>
                                                )}
                                            </span>
                                            <span
                                                className={`text-theme-text-muted text-xs transition-opacity ${
                                                    active ? 'opacity-100' : 'opacity-0'
                                                }`}
                                            >
                                                {I18N.app.jumpTo}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}

                        {scope.live && !loading && liveHits.length === 0 && (
                            <li role="presentation" className="text-theme-text-muted px-2.5 py-2 text-sm">
                                {I18N.app.noResults}
                            </li>
                        )}

                        {/* View-all / press-enter action */}
                        {hasViewAll && (
                            <li role="option" id={`gs-opt-${viewAllIndex}`} aria-selected={viewAllActive}>
                                <button
                                    type="button"
                                    onClick={openResults}
                                    onMouseEnter={() => setActiveIndex(viewAllIndex)}
                                    className={`mt-0.5 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                                        viewAllActive ? 'bg-theme-hover' : ''
                                    } ${scope.live ? 'border-theme-border-divider border-t' : ''}`}
                                >
                                    <span className="material-symbols-rounded text-theme-active text-lg">search</span>
                                    <span className="text-theme-text flex-1 truncate text-sm">
                                        {scope.live ? I18N.app.viewAllResults : I18N.app.pressEnterToSearch}
                                    </span>
                                    <kbd className="bg-theme-content-bg-dark text-theme-text-light/70 hidden rounded-md px-1.5 py-0.5 text-xs font-medium md:block">
                                        return
                                    </kbd>
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
